import React, { useState, Fragment, useEffect, useMemo } from "react";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import API from "../../services/API";
import Select from "react-select";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";

const AnnouncementPage = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [schools, setSchools] = useState([]);
  const [schoolMap, setSchoolMap] = useState({});

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [newAnnouncementTitle, setNewAnnouncementTitle] = useState("");
  const [newAnnouncementBody, setNewAnnouncementBody] = useState("");
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [newAnnouncementImage, setNewAnnouncementImage] = useState(null);
  const [newAnnouncementImagePreview, setNewAnnouncementImagePreview] = useState("");
  const [selectedSchool, setSelectedSchool] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");

  const getHeaders = () => ({
    "Content-Type": "application/json",
    Accept: "application/json",
    token: localStorage.getItem("token"),
  });
  const getUploadHeaders = () => ({
    Accept: "application/json",
    token: localStorage.getItem("token"),
  });

  useEffect(() => {
    getSchools();
  }, []);

  useEffect(() => {
    if (Object.keys(schoolMap).length > 0 || !isLoading) {
      fetchAnnouncements();
    }
  }, [schoolMap, isLoading]);

  const getSchools = () => {
    const headers = getHeaders();

    API.get("/portal/getClasses", { headers })
      .then((response) => {
        if (response.data.response_code === 1000) {
          const data = response.data.data || [];

          const allSchoolsOption = {
            label: "All Schools",
            value: "all",
          };

          const schoolMapping = {};
          let formattedSchools = [];

          if (data.length > 0 && data[0].school_type === "tadika") {
            const uniqueSchoolMap = new Map();

            data.forEach((school) => {
              if (!uniqueSchoolMap.has(school.school_id)) {
                uniqueSchoolMap.set(school.school_id, {
                  label: school.class_name || `School ${school.school_id}`,
                  value: school.school_id,
                });
              }

              schoolMapping[school.school_id] =
                school.class_name || `School ${school.school_id}`;
            });

            formattedSchools = Array.from(uniqueSchoolMap.values());
            setSchools([allSchoolsOption, ...formattedSchools]);
            setSelectedSchool([allSchoolsOption]);
          } else {
            setSchools([allSchoolsOption]);
            setSelectedSchool([allSchoolsOption]);
          }

          setSchoolMap(schoolMapping);
        } else {
          toast.error(response.data.message || "Failed to fetch schools", {
            autoClose: 5000,
          });
        }
      })
      .catch(() => {
        toast.error("Error fetching schools", { autoClose: 5000 });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchAnnouncements = () => {
    const headers = getHeaders();

    API.get("/portal/obtainBulletinAdmin", { headers })
      .then((response) => {
        if (response.data.response_code === 1000) {
          const uniqueAnnouncementsMap = new Map();

          (response.data.data || []).forEach((announcement) => {
            const uniqueKey = `${announcement.title}_${announcement.message}`;

            if (!uniqueAnnouncementsMap.has(uniqueKey)) {
              uniqueAnnouncementsMap.set(uniqueKey, {
                ...announcement,
                schools: [
                  {
                    id: announcement.school_id,
                    name:
                      schoolMap[announcement.school_id] ||
                      (announcement.school_id === "all"
                        ? "All Schools"
                        : "Unknown School"),
                  },
                ],
              });
            } else {
              const existingSchools =
                uniqueAnnouncementsMap.get(uniqueKey).schools || [];

              if (
                !existingSchools.some((s) => s.id === announcement.school_id)
              ) {
                existingSchools.push({
                  id: announcement.school_id,
                  name:
                    schoolMap[announcement.school_id] ||
                    (announcement.school_id === "all"
                      ? "All Schools"
                      : "Unknown School"),
                });
              }
            }
          });

          setAnnouncements(Array.from(uniqueAnnouncementsMap.values()));
        } else {
          toast.error(response.data.message || "Failed to fetch announcements", {
            autoClose: 5000,
          });
        }
      })
      .catch(() => {
        toast.error("Error fetching announcements", { autoClose: 5000 });
      });
  };

  const openAddModal = () => {
    setNewAnnouncementTitle("");
    setNewAnnouncementBody("");
    setNewAnnouncementImage(null);
    setNewAnnouncementImagePreview("");
    setSelectedSchool(
      schools.length > 0 ? [schools.find((s) => s.value === "all")] : []
    );
    setIsAddModalOpen(true);
  };

  const closeAddModal = () => {
    setNewAnnouncementImage(null);
    setNewAnnouncementImagePreview("");
    setIsAddModalOpen(false);
  };

  const openDeleteModal = (announcement) => {
    setSelectedAnnouncement(announcement);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setSelectedAnnouncement(null);
    setIsDeleteModalOpen(false);
  };

  const handleSelectChange = (selectedOptions = []) => {
    if (selectedOptions.some((option) => option.value === "all")) {
      setSelectedSchool([schools.find((s) => s.value === "all")]);
    } else {
      setSelectedSchool(selectedOptions);
    }
  };
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      setNewAnnouncementImage(null);
      setNewAnnouncementImagePreview("");
      return;
    }

    const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];

    if (!allowedTypes.includes(file.type)) {
      toast.error("Only PNG, JPG, JPEG, and WEBP images are allowed", {
        autoClose: 5000,
      });
      e.target.value = "";
      return;
    }

    const maxSizeInMB = 50;
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

    if (file.size > maxSizeInBytes) {
      toast.error("Image must be below 5MB", {
        autoClose: 5000,
      });
      e.target.value = "";
      return;
    }

    setNewAnnouncementImage(file);
    setNewAnnouncementImagePreview(URL.createObjectURL(file));
  };

  const removeAnnouncementImage = () => {
    setNewAnnouncementImage(null);
    setNewAnnouncementImagePreview("");
  };
  const handleAddAnnouncement = () => {
    if (!newAnnouncementTitle.trim() || !newAnnouncementBody.trim()) {
      toast.error("Title and body cannot be empty", { autoClose: 5000 });
      return;
    }

    const selectedSchoolsPayload =
      selectedSchool.length === 0 ||
        selectedSchool.some((school) => school.value === "all")
        ? ["all"]
        : selectedSchool.map((school) => school.value);

    const formData = new FormData();
    formData.append("title", newAnnouncementTitle.trim());
    formData.append("body", newAnnouncementBody.trim());
    formData.append("schools", JSON.stringify(selectedSchoolsPayload));

    if (newAnnouncementImage) {
      formData.append("image", newAnnouncementImage);
    }

    setIsSubmitting(true);

    API.post("/portal/sendBulletin", formData, {
      headers: getUploadHeaders(),
    })
      .then((response) => {
        if (response.status === 200 || response.data.response_code === 1000) {
          toast.success("Announcement added successfully", { autoClose: 5000 });
          closeAddModal();
          fetchAnnouncements();
        } else {
          toast.error(response.data.message || "Failed to add announcement", {
            autoClose: 5000,
          });
        }
      })
      .catch((error) => {
        console.log("Announcement upload error:", error?.response || error);
        toast.error("Error adding announcement", { autoClose: 5000 });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };
  const handleDeleteAnnouncement = () => {
    if (!selectedAnnouncement) return;

    const headers = getHeaders();

    setIsSubmitting(true);

    API.post(
      "/portal/deleteBulletin",
      {
        bulletin_id: selectedAnnouncement.bulletin_id,
      },
      { headers }
    )
      .then((response) => {
        if (response.data.response_code === 1000) {
          toast.success("Announcement deleted successfully", {
            autoClose: 5000,
          });
          closeDeleteModal();
          fetchAnnouncements();
        } else {
          toast.error(response.data.message || "Failed to delete announcement", {
            autoClose: 5000,
          });
        }
      })
      .catch(() => {
        toast.error("Error deleting announcement", { autoClose: 5000 });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const filteredAnnouncements = useMemo(() => {
    const keyword = searchTerm.toLowerCase().trim();

    if (!keyword) return announcements;

    return announcements.filter((announcement) => {
      const title = String(announcement.title || "").toLowerCase();
      const message = String(announcement.message || "").toLowerCase();
      const schoolNames = (announcement.schools || [])
        .map((s) => s.name)
        .join(" ")
        .toLowerCase();

      return (
        title.includes(keyword) ||
        message.includes(keyword) ||
        schoolNames.includes(keyword)
      );
    });
  }, [announcements, searchTerm]);

  const totalSchoolsReached = useMemo(() => {
    const ids = new Set();

    announcements.forEach((announcement) => {
      (announcement.schools || []).forEach((school) => {
        if (school.id) ids.add(school.id);
      });
    });

    return ids.size;
  }, [announcements]);

  const columns = [
    {
      name: "Announcement",
      selector: (row) => row.title,
      sortable: true,
      grow: 3,
      cell: (row) => (
        <div className="py-3">
          <div className="d-flex align-items-start gap-3">
            <div
              className="rounded-4 d-flex align-items-center justify-content-center fw-bold"
              style={{
                width: 44,
                height: 44,
                background: "#fee2e2",
                color: "#991b1b",
                flex: "0 0 auto",
              }}
            >
              A
            </div>

            <div style={{ minWidth: 0 }}>
              <div className="fw-bold text-dark mb-1">{row.title}</div>
              <div
                className="text-muted small"
                style={{
                  maxWidth: 620,
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.5,
                }}
              >
                {row.message}
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      name: "Audience",
      selector: (row) => row.schools?.length || 0,
      sortable: true,
      grow: 2,
      cell: (row) => (
        <div className="d-flex flex-wrap gap-2 py-3">
          {(row.schools || []).slice(0, 3).map((school) => (
            <span
              key={`${row.bulletin_id}-${school.id}`}
              className="badge rounded-pill px-3 py-2"
              style={{
                background: "#f1f5f9",
                color: "#334155",
                fontWeight: 700,
              }}
            >
              {school.name}
            </span>
          ))}

          {(row.schools || []).length > 3 && (
            <span
              className="badge rounded-pill px-3 py-2"
              style={{
                background: "#fee2e2",
                color: "#991b1b",
                fontWeight: 700,
              }}
            >
              +{row.schools.length - 3} more
            </span>
          )}
        </div>
      ),
    },
    {
      name: "Action",
      center: true,
      width: "150px",
      cell: (row) => (
        <button
          className="btn btn-sm btn-outline-danger rounded-4 fw-semibold px-3"
          onClick={() => openDeleteModal(row)}
        >
          Delete
        </button>
      ),
    },
  ];

  const customStyles = {
    table: {
      style: {
        backgroundColor: "#ffffff",
      },
    },
    headRow: {
      style: {
        backgroundColor: "#f8fafc",
        borderBottom: "1px solid #e2e8f0",
        minHeight: "56px",
      },
    },
    headCells: {
      style: {
        color: "#64748b",
        fontSize: "12px",
        fontWeight: 800,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      },
    },
    rows: {
      style: {
        minHeight: "92px",
        borderBottom: "1px solid #eef2f7",
      },
      highlightOnHoverStyle: {
        backgroundColor: "#fff7f7",
        borderBottomColor: "#fee2e2",
        outline: "none",
      },
    },
    pagination: {
      style: {
        borderTop: "1px solid #eef2f7",
        padding: "12px 16px",
      },
    },
  };

  return (
    <Fragment>
      <div
        className="container-fluid py-4"
        style={{
          background: "#f8fafc",
          minHeight: "100vh",
        }}
      >
        {/* Header */}
        <div
          className="rounded-5 p-4 p-md-5 mb-4 position-relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, #7f1d1d 0%, #dc2626 48%, #fb7185 100%)",
            boxShadow: "0 24px 60px rgba(220, 38, 38, 0.18)",
          }}
        >
          <div
            className="position-absolute top-0 end-0 rounded-circle"
            style={{
              width: 260,
              height: 260,
              background: "rgba(255,255,255,0.12)",
              transform: "translate(30%, -35%)",
            }}
          />

          <div
            className="position-absolute bottom-0 start-50 rounded-circle"
            style={{
              width: 180,
              height: 180,
              background: "rgba(255,255,255,0.08)",
              transform: "translate(-50%, 45%)",
            }}
          />

          <div className="position-relative d-flex flex-column flex-lg-row justify-content-between gap-4">
            <div>
              <div
                className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill mb-3"
                style={{
                  background: "rgba(255,255,255,0.16)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.24)",
                  backdropFilter: "blur(10px)",
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                <span
                  className="rounded-circle"
                  style={{
                    width: 8,
                    height: 8,
                    background: "#fff",
                    display: "inline-block",
                  }}
                />
                School Communications
              </div>

              <h1
                className="text-white fw-black mb-2"
                style={{
                  fontSize: "clamp(32px, 4vw, 52px)",
                  letterSpacing: "-1.5px",
                }}
              >
                Announcements
              </h1>

              <p
                className="text-white-50 mb-0"
                style={{
                  maxWidth: 720,
                  fontSize: 16,
                }}
              >
                Create, manage, and broadcast school announcements to parents,
                students, or selected school groups.
              </p>
            </div>

            <div className="d-flex align-items-start gap-2">
              <button
                type="button"
                className="btn border-0 rounded-4 fw-bold px-4 py-3"
                style={{
                  background: "rgba(255,255,255,0.16)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.24)",
                }}
                onClick={fetchAnnouncements}
                disabled={isLoading}
              >
                Refresh
              </button>

              <button
                type="button"
                className="btn border-0 rounded-4 fw-bold px-4 py-3"
                style={{
                  background: "#ffffff",
                  color: "#991b1b",
                  boxShadow: "0 18px 40px rgba(0,0,0,0.12)",
                }}
                onClick={openAddModal}
              >
                Add Announcement
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="row g-3 mb-4">
          <div className="col-md-4">
            <div className="card border-0 rounded-5 shadow-sm h-100">
              <div className="card-body p-4">
                <div className="text-muted small fw-semibold mb-2">
                  Total Announcements
                </div>
                <div className="display-6 fw-black text-dark">
                  {announcements.length}
                </div>
                <div className="text-muted small mt-2">
                  Unique messages created
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="card border-0 rounded-5 shadow-sm h-100">
              <div className="card-body p-4">
                <div className="text-muted small fw-semibold mb-2">
                  Audience Groups
                </div>
                <div className="display-6 fw-black text-danger">
                  {totalSchoolsReached}
                </div>
                <div className="text-muted small mt-2">
                  Schools or groups targeted
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="card border-0 rounded-5 shadow-sm h-100">
              <div className="card-body p-4">
                <div className="text-muted small fw-semibold mb-2">
                  Search Results
                </div>
                <div className="display-6 fw-black text-primary">
                  {filteredAnnouncements.length}
                </div>
                <div className="text-muted small mt-2">
                  Matching announcements
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="card border-0 shadow-sm rounded-5 mb-4">
          <div className="card-body p-4">
            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
              <div>
                <h6 className="fw-black text-dark mb-1">
                  Announcement Library
                </h6>
                <div className="text-muted small">
                  Search by title, message body, or audience group.
                </div>
              </div>

              <div className="d-flex flex-column flex-md-row gap-2">
                <input
                  className="form-control rounded-4"
                  style={{ minWidth: 320 }}
                  placeholder="Search announcements..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />

                {searchTerm && (
                  <button
                    type="button"
                    className="btn btn-light rounded-4 fw-semibold px-4"
                    onClick={() => setSearchTerm("")}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card border-0 shadow-sm rounded-5 overflow-hidden">
          <div className="card-header bg-white border-0 p-4">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
              <div>
                <h5 className="fw-black text-dark mb-1">
                  Published Announcements
                </h5>
                <div className="text-muted small">
                  Showing {filteredAnnouncements.length} of{" "}
                  {announcements.length} announcement records.
                </div>
              </div>

              <div
                className="rounded-pill px-3 py-2 small fw-semibold"
                style={{
                  background: "#f1f5f9",
                  color: "#334155",
                }}
              >
                Admin View
              </div>
            </div>
          </div>

          <DataTable
            columns={columns}
            data={filteredAnnouncements}
            progressPending={isLoading}
            persistTableHead
            pagination
            highlightOnHover
            customStyles={customStyles}
            noDataComponent={
              <div className="text-center py-5">
                <div
                  className="mx-auto mb-3 rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: 70,
                    height: 70,
                    background: "#fee2e2",
                    color: "#dc2626",
                    fontWeight: 900,
                    fontSize: 24,
                  }}
                >
                  A
                </div>

                <div className="fw-bold text-dark mb-1">
                  No announcements found
                </div>

                <div className="text-muted small">
                  Create a new announcement or adjust your search keyword.
                </div>
              </div>
            }
          />
        </div>
      </div>

      {/* Add Announcement Modal */}
      <Modal
        isOpen={isAddModalOpen}
        toggle={closeAddModal}
        centered
        size="lg"
        className="modal-dialog-scrollable"
      >
        <ModalHeader toggle={closeAddModal}>
          <div>
            <div className="text-muted small fw-semibold">
              New Broadcast Message
            </div>
            <div className="fw-black text-dark">Add Announcement</div>
          </div>
        </ModalHeader>

        <ModalBody style={{ background: "#f8fafc" }}>
          <div className="card border-0 rounded-5 shadow-sm">
            <div className="card-body p-4">
              <div className="mb-3">
                <label className="form-label fw-semibold small text-muted">
                  Title
                </label>
                <input
                  type="text"
                  className="form-control form-control-lg rounded-4"
                  placeholder="Example: School Holiday Notice"
                  value={newAnnouncementTitle}
                  onChange={(e) => setNewAnnouncementTitle(e.target.value)}
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold small text-muted">
                  Body
                </label>
                <textarea
                  className="form-control rounded-4"
                  rows="6"
                  placeholder="Write the announcement message..."
                  value={newAnnouncementBody}
                  onChange={(e) => setNewAnnouncementBody(e.target.value)}
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold small text-muted">
                  Image
                </label>

                <div
                  className="rounded-5 p-4"
                  style={{
                    background: "#fff",
                    border: "1px dashed #fecaca",
                  }}
                >
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    className="form-control rounded-4"
                    onChange={handleImageChange}
                  />

                  <div className="text-muted small mt-2">
                    Optional. Upload PNG, JPG, JPEG, or WEBP. Max 50MB.
                  </div>

                  {newAnnouncementImagePreview && (
                    <div className="mt-3">
                      <img
                        src={newAnnouncementImagePreview}
                        alt="Announcement preview"
                        style={{
                          width: "100%",
                          maxHeight: 260,
                          objectFit: "cover",
                          borderRadius: 18,
                          border: "1px solid #fee2e2",
                        }}
                      />

                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger rounded-4 fw-semibold mt-3"
                        onClick={removeAnnouncementImage}
                      >
                        Remove Image
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="form-label fw-semibold small text-muted">
                  Audience
                </label>

                <Select
                  isMulti
                  options={schools}
                  value={selectedSchool}
                  onChange={handleSelectChange}
                  placeholder="Select schools..."
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: 16,
                      borderColor: "#e2e8f0",
                      minHeight: 48,
                      boxShadow: "none",
                    }),
                    multiValue: (base) => ({
                      ...base,
                      borderRadius: 999,
                      background: "#fee2e2",
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: "#991b1b",
                      fontWeight: 700,
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: "#991b1b",
                      ":hover": {
                        background: "#fecaca",
                        color: "#7f1d1d",
                      },
                    }),
                  }}
                />

                <div className="text-muted small mt-2">
                  Select All Schools to broadcast to every available audience.
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            className="rounded-4 fw-semibold px-4"
            onClick={closeAddModal}
            disabled={isSubmitting}
          >
            Cancel
          </Button>

          <Button
            color="danger"
            className="rounded-4 fw-bold px-4"
            onClick={handleAddAnnouncement}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Publishing..." : "Publish Announcement"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Announcement Modal */}
      <Modal isOpen={isDeleteModalOpen} toggle={closeDeleteModal} centered>
        <ModalHeader toggle={closeDeleteModal}>
          <div>
            <div className="text-muted small fw-semibold">
              Delete Confirmation
            </div>
            <div className="fw-black text-dark">Remove Announcement</div>
          </div>
        </ModalHeader>

        <ModalBody>
          <div
            className="rounded-5 p-4"
            style={{
              background: "#fff1f2",
              border: "1px solid #fecdd3",
            }}
          >
            <div className="fw-bold text-dark mb-2">
              Are you sure you want to delete this announcement?
            </div>

            <div className="text-muted small mb-3">
              This action cannot be undone.
            </div>

            <div className="fw-semibold text-danger">
              {selectedAnnouncement?.title}
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            color="light"
            className="rounded-4 fw-semibold px-4"
            onClick={closeDeleteModal}
            disabled={isSubmitting}
          >
            Cancel
          </Button>

          <Button
            color="danger"
            className="rounded-4 fw-bold px-4"
            onClick={handleDeleteAnnouncement}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Deleting..." : "Yes, Delete"}
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default AnnouncementPage;